import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import {
  ClearCommodityRequest,
  ClearCommodityResponse,
  ClearSecurityRequest,
  ClearSecurityResponse,
  CommodityVerificationResponse,
  SecurityScreeningMethodResponse,
} from '../../../models/position-operations/clear-security-screening.model';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class ClearSecurityScreeningService {
  private readonly _http = inject(HttpClient);
  private readonly _commonUtils = inject(CommonUtils);
  private readonly _errorHandler = inject(ErrorhandleService);

  readonly baseUrl = environment.apiUrl.concat(
    '/position-based-operation/clear-security-scan',
  );

  /**
   *Get Security Screening Method
   * @return {*}  {Observable<SecurityScreeningMethodResponse>}
   * @memberof ClearSecurityScreeningService
   */
  getSecurityScreeningMethods(): Observable<SecurityScreeningMethodResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<SecurityScreeningMethodResponse>(
        this.baseUrl.concat('/screening-methods/security-screening'),
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<SecurityScreeningMethodResponse>(
            error,
            error.error.code,
            'ClearSecurityScreening Service: Security Screening Method',
            BANNER_TYPE.CLEAR_SEC_SCREENING,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get Commodity Verification
   * @return {*}  {Observable<CommodityVerificationResponse>}
   * @memberof ClearSecurityScreeningService
   */
  getCommodityVerification(): Observable<CommodityVerificationResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<CommodityVerificationResponse>(
        this.baseUrl.concat('/screening-methods/commodity-verification'),
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<CommodityVerificationResponse>(
            error,
            error.error.code,
            'ClearSecurityScreening Service: Commodity Verification',
            BANNER_TYPE.COMM_VERIF,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *submit clear Security screening
   * @return {*}  {Observable<ClearSecurityResponse>}
   * @memberof ClearSecurityScreeningService
   */
  submitClearSecurity(
    clearSecurityRequest: ClearSecurityRequest,
  ): Observable<ClearSecurityResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<ClearSecurityResponse>(
        this.baseUrl.concat('/submit-clear-security-screening'),
        clearSecurityRequest,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<ClearSecurityResponse>(
            error,
            error.error.code,
            'ClearSecurityScreening Service: Submit clear security',
            BANNER_TYPE.API_400_ERROR,
            {} as ClearSecurityResponse,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *submit clear Commodity verification
   * @return {*}  {Observable<ClearCommodityResponse>}
   * @memberof ClearSecurityScreeningService
   */
  submitClearCommodity(
    clearCommodityRequest: ClearCommodityRequest,
  ): Observable<ClearCommodityResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<ClearCommodityResponse>(
        this.baseUrl.concat('/submit-clear-commodity-verification'),
        clearCommodityRequest,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<ClearCommodityResponse>(
            error,
            error.error.code,
            'ClearSecurityScreening Service: Submit clear commodity verification',
            BANNER_TYPE.API_400_ERROR,
            {} as ClearCommodityResponse,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
